import { DEAL_STATUSES, formatDate, formatMoney, type DealStatus } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Columns3, List, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState, type DragEvent } from 'react';
import { Link, useSearchParams } from 'react-router';

import { DealStatusBadge, NextActivity, StaleBadge } from '../../components/deals/badges.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import { useAssignees, useChannels } from '../../lib/contacts-api.ts';
import {
  dealKeys,
  useBoard,
  useDeals,
  usePipelines,
  type Board,
  type DealFilters,
  type DealSummary,
} from '../../lib/deals-api.ts';
import { useCan, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

const PAGE_SIZE = 25;
const FILTER_KEYS = [
  'q',
  'pipelineId',
  'stageId',
  'status',
  'channelId',
  'assignedMembershipId',
  'createdFrom',
  'createdTo',
  'stale',
  'noNextActivity',
  'page',
] as const;

/** Deals: board by stage (default) or a filtered list. Filters live in the URL. */
export function DealsPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get('vista') === 'lista' ? 'list' : 'board';
  const pipelines = usePipelines();
  const assignees = useAssignees();
  const canDelete = useCan('records.delete');
  const canEdit = useCan('records.edit');
  const pipelineId = params.get('pipelineId') ?? pipelines.data?.[0]?.id;
  const assigned = params.get('assignedMembershipId') ?? undefined;

  const setParam = (values: Record<string, string | undefined>) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(values)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        if (!('page' in values)) next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Negocios</h1>
          <p className="text-sm text-muted-foreground">
            Cada oportunidad, desde el lead hasta ganarla o perderla.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-lg border border-border" role="group" aria-label="Vista">
            <Button
              variant={view === 'board' ? 'secondary' : 'ghost'}
              aria-pressed={view === 'board'}
              onClick={() => {
                setParam({ vista: undefined });
              }}
            >
              <Columns3 /> Tablero
            </Button>
            <Button
              variant={view === 'list' ? 'secondary' : 'ghost'}
              aria-pressed={view === 'list'}
              onClick={() => {
                setParam({ vista: 'lista' });
              }}
            >
              <List /> Lista
            </Button>
          </div>
          {canDelete && (
            <Link to="/negocios/papelera" className={buttonVariants({ variant: 'ghost' })}>
              <Trash2 /> Papelera
            </Link>
          )}
          {canEdit && (
            <Link to="/negocios/nuevo" className={buttonVariants({ variant: 'brand' })}>
              <Plus /> Nuevo lead
            </Link>
          )}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          aria-label="Embudo"
          value={pipelineId ?? ''}
          onChange={(e) => {
            setParam({ pipelineId: e.target.value || undefined, stageId: undefined });
          }}
        >
          {view === 'list' && <option value="">Todos los embudos</option>}
          {(pipelines.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Agente"
          value={assigned ?? ''}
          onChange={(e) => {
            setParam({ assignedMembershipId: e.target.value || undefined });
          }}
        >
          <option value="">Todos los agentes</option>
          {(assignees.data ?? []).map((m) => (
            <option key={m.membershipId} value={m.membershipId}>
              {m.name}
            </option>
          ))}
        </Select>
      </div>

      {view === 'board' ? (
        pipelineId ? (
          <BoardView pipelineId={pipelineId} assigned={assigned} canEdit={canEdit} />
        ) : (
          <Loading />
        )
      ) : (
        <ListView params={params} setParam={setParam} />
      )}
    </div>
  );
}

function DealCard({
  deal,
  stages,
  canEdit,
  onMove,
  onDragStart,
}: {
  deal: DealSummary;
  stages: Board['stages'];
  canEdit: boolean;
  onMove: (deal: DealSummary, stageId: string) => void;
  onDragStart: (deal: DealSummary) => void;
}) {
  const tz = useSession().data?.tenant.timezone;
  return (
    <li
      draggable={canEdit}
      onDragStart={() => {
        onDragStart(deal);
      }}
      className="space-y-2 rounded-lg border border-border bg-surface p-3 shadow-xs"
    >
      <div className="space-y-0.5">
        <Link to={`/negocios/${deal.id}`} className="font-medium text-primary hover:underline">
          {deal.title}
        </Link>
        <p className="text-sm text-muted-foreground">{deal.contact.name}</p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {deal.estimatedAmount !== null && (
          <span className="font-medium text-foreground tabular-nums">
            {formatMoney(deal.estimatedAmount)}
          </span>
        )}
        {deal.stale && <StaleBadge />}
        <span>{deal.assignedTo.name}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        <NextActivity deal={deal} tz={tz} />
      </div>
      {canEdit && (
        <Select
          aria-label={`Mover «${deal.title}» a otra etapa`}
          className="min-h-9 text-sm"
          value={deal.stage.id}
          onChange={(e) => {
            onMove(deal, e.target.value);
          }}
        >
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      )}
    </li>
  );
}

function BoardView({
  pipelineId,
  assigned,
  canEdit,
}: {
  pipelineId: string;
  assigned: string | undefined;
  canEdit: boolean;
}) {
  const board = useBoard(pipelineId, assigned);
  const client = useQueryClient();
  const notify = useToast();
  const [dragged, setDragged] = useState<DealSummary | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const move = async (deal: DealSummary, stageId: string) => {
    if (deal.stage.id === stageId) return;
    try {
      await api(`/deals/${deal.id}/stage`, { method: 'POST', body: { stageId } });
      await client.invalidateQueries({ queryKey: dealKeys.all });
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const onDrop = (event: DragEvent, stageId: string) => {
    event.preventDefault();
    setOver(null);
    if (dragged) void move(dragged, stageId);
    setDragged(null);
  };

  if (board.isPending) return <Loading />;
  if (board.error) return <Alert tone="danger">{errorMessage(board.error)}</Alert>;
  const stages = board.data.stages;
  return (
    // Scrolls sideways: focusable, so the keyboard can scroll it too.
    <div
      className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0"
      role="region"
      aria-label={`Tablero de ${board.data.pipeline.name}`}
      tabIndex={0}
      aria-busy={board.isFetching}
    >
      <div className="flex gap-3">
        {stages.map((stage) => (
          <section
            key={stage.id}
            aria-label={`Etapa ${stage.name}`}
            onDragOver={(event) => {
              if (dragged) {
                event.preventDefault();
                setOver(stage.id);
              }
            }}
            onDragLeave={() => {
              setOver((current) => (current === stage.id ? null : current));
            }}
            onDrop={(event) => {
              onDrop(event, stage.id);
            }}
            className={cn(
              'flex w-72 shrink-0 flex-col gap-2 rounded-xl border border-border bg-muted/60 p-2',
              over === stage.id && 'ring-2 ring-primary',
            )}
          >
            <header className="px-1 pt-1">
              <h2 className="flex items-center justify-between text-sm font-semibold">
                <span>{stage.name}</span>
                <span className="text-muted-foreground tabular-nums">{stage.total}</span>
              </h2>
              <p className="text-xs text-muted-foreground">
                {stage.probability} %{stage.amount > 0 ? ` · ${formatMoney(stage.amount)}` : ''}
              </p>
            </header>
            <ul className="flex min-h-24 flex-col gap-2">
              {stage.deals.map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  stages={stages}
                  canEdit={canEdit}
                  onMove={(d, s) => void move(d, s)}
                  onDragStart={setDragged}
                />
              ))}
              {stage.deals.length === 0 && (
                <li className="rounded-lg border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
                  Sin negocios
                </li>
              )}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

function ListView({
  params,
  setParam,
}: {
  params: URLSearchParams;
  setParam: (values: Record<string, string | undefined>) => void;
}) {
  const { data: session } = useSession();
  const filters = Object.fromEntries(
    FILTER_KEYS.flatMap((k) => {
      const value = params.get(k);
      return value ? [[k, k === 'page' ? Number(value) : value]] : [];
    }),
  ) as DealFilters;
  const page = filters.page ?? 1;
  const deals = useDeals({ ...filters, page });
  const channels = useChannels();
  const pipelines = usePipelines();
  const [search, setSearch] = useState(filters.q ?? '');
  const stages = pipelines.data?.find((p) => p.id === filters.pipelineId)?.stages ?? [];

  useEffect(() => {
    const timer = setTimeout(() => {
      if ((params.get('q') ?? '') !== search.trim()) setParam({ q: search.trim() || undefined });
    }, 300);
    return () => {
      clearTimeout(timer);
    };
    // setParam changes every render; the search text is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const total = deals.data?.total ?? 0;
  const items = deals.data?.items ?? [];
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  return (
    <>
      <div className="space-y-3 rounded-xl border border-border bg-surface p-3 sm:p-4">
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            aria-label="Buscar por negocio o cliente"
            placeholder="Buscar por negocio o cliente"
            className="pl-9"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            aria-label="Etapa"
            value={filters.stageId ?? ''}
            disabled={stages.length === 0}
            onChange={(e) => {
              setParam({ stageId: e.target.value || undefined });
            }}
          >
            <option value="">
              {stages.length ? 'Todas las etapas' : 'Elige un embudo para filtrar etapa'}
            </option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Estado"
            value={filters.status ?? ''}
            onChange={(e) => {
              setParam({ status: e.target.value || undefined });
            }}
          >
            <option value="">Todos los estados</option>
            {(Object.entries(DEAL_STATUSES) as [DealStatus, string][]).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Canal"
            value={filters.channelId ?? ''}
            onChange={(e) => {
              setParam({ channelId: e.target.value || undefined });
            }}
          >
            <option value="">Todos los canales</option>
            {(channels.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-5"
              checked={filters.stale === 'true'}
              onChange={(e) => {
                setParam({ stale: e.target.checked ? 'true' : undefined });
              }}
            />
            Solo estancados
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-5"
              checked={filters.noNextActivity === 'true'}
              onChange={(e) => {
                setParam({ noNextActivity: e.target.checked ? 'true' : undefined });
              }}
            />
            Sin próxima actividad
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-2">
            Creados desde
            <Input
              type="date"
              className="w-auto"
              value={filters.createdFrom ?? ''}
              onChange={(e) => {
                setParam({ createdFrom: e.target.value || undefined });
              }}
            />
          </label>
          <label className="flex items-center gap-2">
            hasta
            <Input
              type="date"
              className="w-auto"
              value={filters.createdTo ?? ''}
              onChange={(e) => {
                setParam({ createdTo: e.target.value || undefined });
              }}
            />
          </label>
        </div>
      </div>
      {deals.isPending ? (
        <Loading />
      ) : deals.error ? (
        <Alert tone="danger">{errorMessage(deals.error)}</Alert>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <p className="font-medium">Ningún negocio coincide.</p>
          <p className="text-sm text-muted-foreground">Prueba con otra búsqueda o quita filtros.</p>
        </div>
      ) : (
        <>
          <ul
            className={cn(
              'divide-y divide-border rounded-xl border border-border bg-surface',
              deals.isFetching && 'opacity-70',
            )}
          >
            {items.map((d) => (
              <li key={d.id} className="space-y-1 p-3 sm:p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <Link
                    to={`/negocios/${d.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {d.title}
                  </Link>
                  {d.estimatedAmount !== null && (
                    <span className="font-semibold tabular-nums">
                      {formatMoney(d.estimatedAmount)}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <DealStatusBadge status={d.status} />
                  {d.stale && <StaleBadge />}
                  <span>
                    {d.pipeline.name} · {d.stage.name}
                  </span>
                  <span>· {d.contact.name}</span>
                  <span>· {d.assignedTo.name}</span>
                  {d.channel && <span>· {d.channel.name}</span>}
                  <span className="hidden sm:inline">
                    · Creado {formatDate(d.createdAt, session?.tenant.timezone)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  <NextActivity deal={d} tz={session?.tenant.timezone} />
                </div>
              </li>
            ))}
          </ul>
          <nav aria-label="Páginas" className="flex items-center justify-between text-sm">
            <p className="text-muted-foreground">
              {from}–{to} de {total}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={page <= 1}
                onClick={() => {
                  setParam({ page: String(page - 1) });
                }}
              >
                <ChevronLeft /> Anterior
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={to >= total}
                onClick={() => {
                  setParam({ page: String(page + 1) });
                }}
              >
                Siguiente <ChevronRight />
              </Button>
            </div>
          </nav>
        </>
      )}
    </>
  );
}
